import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import Highlight from "svelte-highlight/Highlight.svelte";
import HighlightSvelte from "svelte-highlight/HighlightSvelte.svelte";
import bash from "svelte-highlight/languages/bash";
import css from "svelte-highlight/languages/css";
import javascript from "svelte-highlight/languages/javascript";
import json from "svelte-highlight/languages/json";
import markdown from "svelte-highlight/languages/markdown";
import python from "svelte-highlight/languages/python";
import sql from "svelte-highlight/languages/sql";
import typescript from "svelte-highlight/languages/typescript";
import xml from "svelte-highlight/languages/xml";
import yaml from "svelte-highlight/languages/yaml";

var root = $.from_html(`<pre><code class="hljs language-markdown"></code></pre>`);
var root_1 = $.from_html(`<pre><code> </code></pre>`);

export default function HighlightedMarkdownCode($$anchor, $$props) {
	$.push($$props, true);

	const languages = {
		bash,
		css,
		javascript,
		json,
		markdown,
		python,
		sql,
		typescript,
		xml,
		yaml
	};

	const aliases = {
		cjs: "javascript",
		html: "xml",
		js: "javascript",
		jsx: "javascript",
		md: "markdown",
		mjs: "javascript",
		py: "python",
		sh: "bash",
		shell: "bash",
		slex: "slex",
		ts: "typescript",
		tsx: "typescript",
		yml: "yaml"
	};

	const slexKeywords = new Set([
		"const",
		"false",
		"g",
		"layout",
		"let",
		"namespace",
		"null",
		"return",
		"true",
		"undefined"
	]);

	const slexPattern = /\/\/[^\n]*|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b\d+(?:\.\d+)?\b|\b[$A-Z_a-z][$\w]*\b|[{}[\]():,.]|\r\n|\r|\n|./g;

	let lang = $.prop($$props, 'lang', 3, ""),
		text = $.prop($$props, 'text', 3, ""),
		className = $.prop($$props, 'className', 3, "");

	const languageName = $.derived(() => normalizeLanguage(lang()));
	const language = $.derived(() => $.get(languageName) === "slex" ? typescript : languages[$.get(languageName)]);
	const highlightedMarkdown = $.derived(() => $.get(languageName) === "markdown" ? highlightMarkdownSlexFences(text()) : "");

	const blockClass = $.derived(() => [
		"slex-markdown-code",
		$.get(languageName) ? `language-${$.get(languageName)}` : "",
		className()
	].filter(Boolean).join(" "));

	function normalizeLanguage(value) {
		const [language = ""] = String(value ?? "").trim().toLowerCase().split(/\s+/, 1);

		return aliases[language] ?? language;
	}

	function highlightMarkdownSlexFences(source) {
		const fencePattern = /(```+)(slex)([^\n]*)(\r?\n)([\s\S]*?)(\r?\n\1)/gi;
		let result = "";
		let lastIndex = 0;

		for (const match of source.matchAll(fencePattern)) {
			result += escapeHtml(source.slice(lastIndex, match.index));
			result += `<span class="hljs-section">${escapeHtml(match[1])}</span><span class="hljs-keyword">${escapeHtml(match[2])}</span>${escapeHtml(match[3])}${escapeHtml(match[4])}`;
			result += highlightSlexSource(match[5]);
			result += `<span class="hljs-section">${escapeHtml(match[6])}</span>`;
			lastIndex = (match.index ?? 0) + match[0].length;
		}

		result += escapeHtml(source.slice(lastIndex));

		return result;
	}

	function highlightSlexSource(source) {
		return Array.from(source.matchAll(slexPattern), ([token]) => {
			const escaped = escapeHtml(token);

			if (token.startsWith("//") || token.startsWith("/*")) return `<span class="hljs-comment">${escaped}</span>`;
			if ((/^["'`]/).test(token)) return `<span class="hljs-string">${escaped}</span>`;
			if ((/^\d/).test(token)) return `<span class="hljs-number">${escaped}</span>`;
			if (slexKeywords.has(token)) return `<span class="hljs-keyword">${escaped}</span>`;
			if ((/^[{}[\]():,.]$/).test(token)) return `<span class="hljs-punctuation">${escaped}</span>`;

			return escaped;
		}).join("");
	}

	function escapeHtml(value) {
		return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
	}

	var fragment = $.comment();
	var node = $.first_child(fragment);

	{
		var consequent = ($$anchor) => {
			HighlightSvelte($$anchor, {
				get code() {
					return text();
				},

				get class() {
					return $.get(blockClass);
				}
			});
		};

		var consequent_1 = ($$anchor) => {
			var pre = root();
			var code = $.child(pre);

			$.html(code, () => $.get(highlightedMarkdown), true);
			$.reset(code);
			$.reset(pre);
			$.template_effect(() => $.set_class(pre, 1, $.clsx($.get(blockClass))));
			$.append($$anchor, pre);
		};

		var d = $.derived(() => $.get(languageName) === "markdown" && (/```+slex/i).test(text()));

		var consequent_2 = ($$anchor) => {
			Highlight($$anchor, {
				get language() {
					return $.get(language);
				},

				get code() {
					return text();
				},

				get class() {
					return $.get(blockClass);
				}
			});
		};

		var alternate = ($$anchor) => {
			var pre_1 = root_1();
			var code_1 = $.child(pre_1);
			var text_1 = $.child(code_1, true);

			$.reset(code_1);
			$.reset(pre_1);

			$.template_effect(() => {
				$.set_class(pre_1, 1, $.clsx($.get(blockClass)));
				$.set_text(text_1, text());
			});

			$.append($$anchor, pre_1);
		};

		$.if(node, ($$render) => {
			if ($.get(languageName) === "svelte") $$render(consequent); else if ($.get(d)) $$render(consequent_1, 1); else if ($.get(language)) $$render(consequent_2, 2); else $$render(alternate, -1);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}